/**
 * Fehlermeldungen aus der laufenden Anwendung.
 *
 * Gebaut für die Alphaphase: die Kollegen arbeiten parallel zum Altsystem und
 * melden, was ihnen auffällt. Der Knopf nimmt den Kontext selbst mit — Seite,
 * Mandant, Fassung, Browser —, weil eine Meldung ohne ihn fast immer eine
 * Rückfrage kostet.
 *
 * Melden braucht kein Recht, nur eine Mitgliedschaft im Mandanten: wer erst
 * ein Recht zugewiesen bekommen muss, meldet nicht. Bearbeiten ist
 * Administration und braucht `admin.bugs.manage`.
 */

/** Wie schlimm ist es? Bewusst drei Stufen — bei fünf wird nach Gefühl gewählt. */
export type BugSeverity = 'blocker' | 'stoerend' | 'schoenheit';

export type BugStatus = 'neu' | 'in_arbeit' | 'behoben' | 'kein_fehler';

export const bugSeverities: readonly BugSeverity[] = ['blocker', 'stoerend', 'schoenheit'];
export const bugStatuses: readonly BugStatus[] = ['neu', 'in_arbeit', 'behoben', 'kein_fehler'];

export const bugSeverityLabels: Readonly<Record<BugSeverity, string>> = {
  blocker: 'Ich komme nicht weiter',
  stoerend: 'Stört, aber ich kann weiterarbeiten',
  schoenheit: 'Schönheitsfehler',
};

export const bugStatusLabels: Readonly<Record<BugStatus, string>> = {
  neu: 'Neu',
  in_arbeit: 'In Arbeit',
  behoben: 'Behoben',
  kein_fehler: 'Kein Fehler',
};

/** Was der Melder selbst schreibt. */
export interface BugReportInput {
  readonly title: string;
  readonly description: string;
  readonly severity: BugSeverity;
}

/**
 * Was die Anwendung automatisch anhängt.
 *
 * Alles optional in dem Sinne, dass eine Meldung ohne Browserkennung immer
 * noch eine Meldung ist — aber die Oberfläche füllt es, weil genau diese vier
 * Angaben die Rückfragen ersparen.
 */
export interface BugReportContext {
  readonly route: string;
  readonly appVersion: string;
  readonly userAgent: string;
  readonly viewport: string;
}

/** Wie der Melder seine eigene Meldung sieht. */
export interface OwnBugReport {
  readonly id: string;
  readonly title: string;
  readonly description: string;
  readonly severity: BugSeverity;
  readonly status: BugStatus;
  readonly route: string;
  readonly resolutionNote: string;
  readonly createdAt: string;
  readonly handledAt: string | null;
}

/** Wie die Administration sie sieht — mit Melder und vollem Kontext. */
export interface BugReportRecord extends OwnBugReport {
  readonly reportedByName: string;
  readonly handledByName: string | null;
  readonly appVersion: string;
  readonly userAgent: string;
  readonly viewport: string;
}

// ---------------------------------------------------------------------------
// Aus JSON lesen
// ---------------------------------------------------------------------------
// Defensiv, wie überall in platform-data: eine kaputte Zeile darf die Liste
// nicht zum Absturz bringen, und ein später ergänztes Feld darf einen alten
// Client nicht stören.

function text(value: unknown, cap = 4000): string {
  return typeof value === 'string' ? value.slice(0, cap) : '';
}

export function normalizeSeverity(value: unknown): BugSeverity {
  return bugSeverities.includes(value as BugSeverity) ? (value as BugSeverity) : 'stoerend';
}

export function normalizeStatus(value: unknown): BugStatus {
  return bugStatuses.includes(value as BugStatus) ? (value as BugStatus) : 'neu';
}

export function normalizeOwnBugReport(value: unknown): OwnBugReport | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  const id = text(row.id, 64);
  if (!id) return null;
  return {
    id,
    title: text(row.title, 160),
    description: text(row.description),
    severity: normalizeSeverity(row.severity),
    status: normalizeStatus(row.status),
    route: text(row.route, 400),
    resolutionNote: text(row.resolution_note, 2000),
    createdAt: text(row.created_at, 40),
    handledAt: typeof row.handled_at === 'string' ? row.handled_at : null,
  };
}

export function normalizeBugReport(value: unknown): BugReportRecord | null {
  const own = normalizeOwnBugReport(value);
  if (!own) return null;
  const row = value as Record<string, unknown>;
  return {
    ...own,
    // Ein gelöschter Benutzer hinterlässt eine Meldung ohne Namen. Sie bleibt
    // trotzdem sichtbar -- der Fehler ist ja nicht mit ihm gegangen.
    reportedByName: text(row.reported_by_name, 160) || 'Unbekannt',
    handledByName: typeof row.handled_by_name === 'string' ? row.handled_by_name : null,
    appVersion: text(row.app_version, 80),
    userAgent: text(row.user_agent, 400),
    viewport: text(row.viewport, 40),
  };
}

export function normalizeBugReports(value: unknown): BugReportRecord[] {
  return Array.isArray(value)
    ? value.map(normalizeBugReport).filter((row): row is BugReportRecord => row !== null)
    : [];
}

export function normalizeOwnBugReports(value: unknown): OwnBugReport[] {
  return Array.isArray(value)
    ? value.map(normalizeOwnBugReport).filter((row): row is OwnBugReport => row !== null)
    : [];
}

/**
 * Reicht das, was der Melder geschrieben hat?
 *
 * Dieselben Grenzen wie in der Datenbank. Sie hier zu wiederholen ist kein
 * doppelter Riegel, sondern der Unterschied zwischen einer Meldung im Formular
 * ("bitte noch zwei Sätze") und einer Fehlermeldung vom Server, nachdem der
 * Text schon weg ist.
 */
export function bugReportProblem(input: BugReportInput): string | null {
  if (input.title.trim().length < 3) return 'Bitte eine kurze Überschrift angeben.';
  if (input.description.trim().length < 10) return 'Bitte beschreiben, was passiert ist — zwei Sätze reichen.';
  return null;
}

/** Der Kontext, den der Browser selbst kennt. */
export function currentBugContext(appVersion: string): BugReportContext {
  const canView = typeof window !== 'undefined';
  return {
    route: canView ? `${window.location.pathname}${window.location.search}` : '',
    appVersion,
    userAgent: canView ? window.navigator.userAgent.slice(0, 400) : '',
    viewport: canView ? `${window.innerWidth}x${window.innerHeight}` : '',
  };
}

export interface BugReportGateway {
  /** Melden. Jedes Mitglied des Mandanten darf das. */
  report(tenantId: string, input: BugReportInput, context: BugReportContext): Promise<void>;
  /** Die eigenen Meldungen samt Stand — damit niemand zweimal meldet. */
  listMine(tenantId: string): Promise<OwnBugReport[]>;
  /** Alle Meldungen des Mandanten (braucht `admin.bugs.view`). */
  listAll(tenantId: string): Promise<BugReportRecord[]>;
  /** Stand setzen (braucht `admin.bugs.manage`). */
  setStatus(tenantId: string, reportId: string, status: BugStatus, note: string): Promise<BugReportRecord[]>;
}
